import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getPlayers } from '../services/api';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

const StatBar = ({ label, value, color }) => {
  const pct = Math.min(100, Math.max(0, Number(value)||0));
  return (
    <div style={{ background:color, borderRadius:12, padding:'16px 18px', flex:1, minWidth:120 }}>
      <div style={{ fontSize:26, fontWeight:700, color:'#000', lineHeight:1 }}>{pct}%</div>
      <div style={{ fontSize:10, color:'rgba(0,0,0,0.6)', marginTop:6, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700 }}>{label}</div>
      <div style={{ marginTop:8, height:4, borderRadius:2, background:'rgba(0,0,0,0.15)' }}>
        <div style={{ width:`${pct}%`, height:'100%', borderRadius:2, background:'rgba(0,0,0,0.3)' }} />
      </div>
    </div>
  );
};

function PlayerModal({ player: p, onClose, loading, onSuspend, onDelete }) {
  if (!p) return null;
  const tier = p.evaluationTier || p.tier;
  const details = [
    ['Nationality',     p.nationality],
    ['Age',             p.age ? `${p.age} yrs` : null],
    ['Weight',          p.weight ? `${p.weight} Kg` : null],
    ['Height',          p.height ? `${p.height}m` : null],
    ['Set Position',    p.position],
    ['Contract Status', p.contractStatus],
    ['F.Foot',          p.preferredFoot],
    ['Location',        p.city || p.stateOfResidence || p.nationality],
    ['Agent / Representative', p.agent?.name || p.agent?.displayName],
    ['Evaluations',     p.evaluations != null ? String(p.evaluations) : null],
    ['Joined',          fmtDate(p.createdAt)],
  ];
  const toPercent = v => v != null ? Math.round((Number(v)/10)*100) : 0;
  const stats = [
    { label:'Technical',     value: toPercent(p.avgTechnical),        color:'#E8503A' },
    { label:'Physical',      value: toPercent(p.avgPhysical),         color:'#4B9EF5' },
    { label:'Tactical',      value: toPercent(p.avgTactical),         color:'#F5A623' },
    { label:'Mental',        value: toPercent(p.avgMental),           color:'#9B59B6' },
    { label:'Potential',     value: toPercent(p.avgPotential),        color:'#2ECC71' },
  ];
  const attrs = [
    ['Ball Control',      p.avgBallControl],
    ['Passing Accuracy',  p.avgPassingAccuracy],
    ['Speed & Agility',   p.avgSpeedAgility],
    ['Strength',          p.avgStrength],
    ['Stamina',           p.avgStamina],
    ['Positioning',       p.avgPositioning],
    ['Decision Making',   p.avgDecisionMaking],
    ['Attitude / Work Ethic', p.avgAttitudeWorkEthic || p.avgAttitude],
    ['Growth Outlook',    p.avgGrowthOutlook],
  ].filter(([,v]) => v != null);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'32px 16px', overflowY:'auto' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:700, border:'1px solid rgba(255,255,255,0.07)', overflow:'hidden', marginBottom:32 }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:50, height:50, borderRadius:'50%', flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background: p.profileImage ? 'transparent' : 'linear-gradient(135deg,#A7F432,#5a9e1a)', fontSize:16, fontWeight:700, color:'#000' }}>
              {p.profileImage ? <img src={p.profileImage} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(p.name || `${p.firstName} ${p.lastName}`)}
            </div>
            <div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:18, color:'var(--text-primary)', fontWeight:600 }}>{p.name || `${p.firstName||''} ${p.lastName||''}`.trim()}</div>
              <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:2 }}>{p.position || '—'} · {p.email}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {tier && <span style={{ background:'rgba(167,244,50,0.12)', color:'#A7F432', border:'1px solid rgba(167,244,50,0.3)', borderRadius:20, padding:'3px 12px', fontSize:11, fontWeight:600 }}>{tier}</span>}
            {p.rating && <span style={{ color:'#F5A623', fontSize:13 }}>★ {parseFloat(p.rating).toFixed(1)}</span>}
            <button onClick={() => onSuspend && onSuspend(p, p.isActive)}
              style={{ background: p.isActive ? 'rgba(255,80,80,0.12)' : 'rgba(167,244,50,0.12)',
                color: p.isActive ? '#ff6b6b' : '#A7F432',
                border: `1px solid ${p.isActive ? 'rgba(255,80,80,0.3)' : 'rgba(167,244,50,0.3)'}`,
                borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
              {p.isActive ? 'Suspend' : 'Unsuspend'}
            </button>
            <button onClick={() => onDelete && onDelete(p)}
              style={{ background:'rgba(255,50,50,0.12)', color:'#ff3333', border:'1px solid rgba(255,50,50,0.3)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
              Delete
            </button>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', color:'var(--text-dim)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 12px', fontSize:13, cursor:'pointer' }}>✕</button>
          </div>
        </div>

        {/* Profile Details */}
        <div style={{ padding:'20px 24px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-primary)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Player Profile Details</div>
            <span style={{ background:'rgba(255,165,0,0.12)', color:'#FFA500', border:'1px solid rgba(255,165,0,0.3)', borderRadius:20, padding:'3px 12px', fontSize:11, fontWeight:600 }}>
              {p.isPendingEvaluation ? 'Pending' : p.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden', marginBottom:20 }}>
            {details.map(([label, value], i) => (
              <div key={label} style={{ display:'flex', borderBottom: i<details.length-1 ? '1px solid rgba(255,255,255,0.05)' : 'none', padding:'11px 16px' }}>
                <div style={{ width:150, fontSize:12, color:'var(--text-dim)', flexShrink:0 }}>{label}</div>
                <div style={{ fontSize:13, color: value ? 'var(--text-primary)' : 'var(--text-dim)' }}>{value || '—'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stat Bars */}
        <div style={{ padding:'0 24px 20px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Performance</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {stats.map(s => <StatBar key={s.label} {...s} />)}
          </div>
        </div>

        {/* Attribute breakdown */}
        {attrs && attrs.length > 0 && (
          <div style={{ padding:'0 24px 20px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Attribute Breakdown</div>
            <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden' }}>
              {attrs.map(([label, value], i) => (
                <div key={label} style={{ padding:'10px 16px', borderBottom: i<attrs.length-1 ? '1px solid rgba(255,255,255,0.05)' : 'none', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:160, fontSize:12, color:'var(--text-dim)', flexShrink:0 }}>{label}</div>
                  <div style={{ flex:1, height:6, background:'rgba(255,255,255,0.08)', borderRadius:3 }}>
                    <div style={{ width:`${Math.round((Number(value)/10)*100)}%`, height:'100%', borderRadius:3, background:'#A7F432' }} />
                  </div>
                  <div style={{ width:30, fontSize:12, color:'var(--text-primary)', fontWeight:600, textAlign:'right' }}>{value}/10</div>
                </div>
              ))}
            </div>
            {p.scoutCount > 0 && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:8 }}>Based on {p.scoutCount} scout rating{p.scoutCount>1?'s':''}{p.lastRatedAt ? ` · Last rated ${new Date(p.lastRatedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}` : ''}</div>}
          </div>
        )}

        {/* Evaluators Section */}
        {p.evaluations && p.evaluations.length > 0 && (
          <div style={{ padding:'0 24px 20px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Evaluators ({p.evaluations.length})</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {p.evaluations.map((ev, i) => (
                <div key={ev.id||i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'12px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#A7F432,#6aad00)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#000' }}>
                      {ev.scoutPfp ? <img src={ev.scoutPfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : ((ev.scoutFirstName||'?')[0]+(ev.scoutLastName||'')[0]||'').toUpperCase()}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{ev.scoutFirstName} {ev.scoutLastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{ev.createdAt ? new Date(ev.createdAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : ''}</div>
                    </div>
                    {ev.recommendTrial && <span style={{ fontSize:10, padding:'2px 8px', borderRadius:20, background:'rgba(167,244,50,0.12)', color:'#A7F432', border:'1px solid rgba(167,244,50,0.2)' }}>Trial Recommended</span>}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6, marginBottom:8 }}>
                    {[['Ball Control',ev.ballControl],['Passing',ev.passingAccuracy],['Speed',ev.speedAgility],['Strength',ev.strength],['Positioning',ev.positioning],['Decision',ev.decisionMaking],['Attitude',ev.attitudeWorkEthic],['Growth',ev.growthOutlook]].map(([label,val]) => val != null && (
                      <div key={label} style={{ background:'rgba(255,255,255,0.04)', borderRadius:6, padding:'5px 8px', textAlign:'center' }}>
                        <div style={{ fontSize:9, color:'rgba(255,255,255,0.4)', marginBottom:2 }}>{label}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#A7F432' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {ev.comments && <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', fontStyle:'italic', borderTop:'1px solid rgba(255,255,255,0.05)', paddingTop:8 }}>"{ev.comments}"</div>}
                  {ev.overallImpression && <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>Impression: {ev.overallImpression}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div style={{ padding:'0 24px 16px', textAlign:'center', fontSize:12, color:'var(--text-dim)' }}>Loading media…</div>
        )}

        {/* Highlight Reels */}
        {!loading && p.highlightReels && p.highlightReels.length > 0 && (
          <div style={{ padding:'0 24px 20px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Highlight Reels</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {p.highlightReels.map((url, i) => (
                <video key={i} src={url} controls style={{ width:200, height:120, borderRadius:8, background:'#000', objectFit:'cover' }} />
              ))}
            </div>
          </div>
        )}

        {/* Career Shots */}
        {!loading && p.careerShots && p.careerShots.length > 0 && (
          <div style={{ padding:'0 24px 24px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Career Shots</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {p.careerShots.map((url, i) => (
                url.match(/\.(mp4|mov|webm)/i)
                  ? <video key={i} src={url} controls style={{ width:180, height:110, borderRadius:8, background:'#000' }} />
                  : <img key={i} src={url} alt="" style={{ width:180, height:110, borderRadius:8, objectFit:'cover', background:'#1a2a2a' }} />
              ))}
            </div>
          </div>
        )}

        {/* Bio */}
        {p.bio && (
          <div style={{ padding:'0 24px 24px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Bio</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', lineHeight:1.7, background:'rgba(255,255,255,0.03)', borderRadius:10, padding:'14px 16px' }}>{p.bio}</div>
          </div>
        )}

      </div>
    </div>
  );
}

export default function Players({ toast }) {
  const { token } = useApp();
  const [players,       setPlayers]       = useState([]);
  const [pagination,    setPagination]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [query,         setQuery]         = useState('');
  const [selected,      setSelected]      = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (p=1, q='') => {
    setLoading(true);
    try {
      const res = await getPlayers(token, { page:p, limit:20, ...(q?{search:q}:{}) });
      setPlayers(res.data||[]);
      setPagination(res.pagination||null);
    } catch(e) { toast?.(e.message,'error'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(1,''); }, [load]);

  const handleSearch = e => { e.preventDefault(); setPage(1); setQuery(search); load(1,search); };

  const openPlayer = async (pl) => {
    setSelected(pl);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/players/${pl.id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success && data.data) setSelected(data.data);
    } catch(e) {}
    finally { setDetailLoading(false); }
  };

  const deletePlayer = async (pl) => {
    if (!window.confirm(`Delete ${pl.firstName} ${pl.lastName}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/players/${pl.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast?.('Player deleted successfully');
        setSelected(null);
        setPlayers(prev => prev.filter(p => p.id !== pl.id));
      } else {
        const d = await res.json();
        toast?.(d.message || 'Failed to delete player');
      }
    } catch (e) { toast?.('Error deleting player'); }
  };

  const suspendPlayer = async (pl, isCurrentlyActive) => {
    try {
      const res = await fetch(`/api/players/${pl.id}/suspend`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !isCurrentlyActive })
      });
      const data = await res.json();
      if (data.success) {
        toast?.(`Player ${isCurrentlyActive ? 'suspended' : 'unsuspended'} successfully`);
        setSelected(prev => prev ? { ...prev, isActive: !isCurrentlyActive } : prev);
        setPlayers(ps => ps.map(p => p.id === pl.id ? { ...p, isActive: !isCurrentlyActive } : p));
      } else { toast?.('Action failed', 'error'); }
    } catch(e) { toast?.('Action failed', 'error'); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Players</h1>
          <p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>All registered players on the platform</p>
        </div>
        {pagination && <div style={{ fontSize:12, color:'var(--text-dim)', background:'var(--bg-card)', padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)' }}>{pagination.total} players</div>}
      </div>

      <form className="toolbar" onSubmit={handleSearch} style={{ marginBottom:16 }}>
        <input className="search-input" placeholder="Search by name, email, position..." value={search} onChange={e=>setSearch(e.target.value)} />
        <button type="submit" className="btn btn-ghost">Search</button>
        {query && <button type="button" className="btn btn-ghost" onClick={()=>{setSearch('');setQuery('');setPage(1);load(1,'');}}>Clear</button>}
      </form>

      <div className="card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span className="card-title">All Players</span>
          {pagination && <span style={{ fontSize:12, color:'var(--text-dim)' }}>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>}
        </div>
        {loading ? (
          <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading players...</div>
        ) : players.length===0 ? (
          <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>{query ? `No players matching "${query}"` : 'No players found'}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Player</th><th>Position</th><th>Nationality</th><th>Rating</th><th>Tier</th><th>Status</th><th>Joined</th><th></th></tr>
              </thead>
              <tbody>
                {players.map(pl => {
                  const tier = pl.evaluationTier || pl.tier;
                  const name = pl.name || `${pl.firstName||''} ${pl.lastName||''}`.trim();
                  return (
                    <tr key={pl.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:32, height:32, borderRadius:'50%', flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background: pl.profileImage ? 'transparent' : 'linear-gradient(135deg,#A7F432,#5a9e1a)', fontSize:11, fontWeight:700, color:'#000' }}>
                            {pl.profileImage ? <img src={pl.profileImage} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(name)}
                          </div>
                          <div>
                            <div style={{ fontWeight:500, fontSize:13 }}>{name}</div>
                            <div style={{ fontSize:11, color:'var(--text-dim)' }}>{pl.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color:'var(--text-muted)', fontSize:12 }}>{pl.position||'—'}</td>
                      <td style={{ color:'var(--text-muted)', fontSize:12 }}>{pl.nationality||'—'}</td>
                      <td style={{ color:'#F5A623', fontSize:12, fontWeight:600 }}>{pl.rating ? `★ ${parseFloat(pl.rating).toFixed(1)}` : '—'}</td>
                      <td>
                        <span style={{ background: tier ? 'rgba(167,244,50,0.12)' : 'rgba(255,255,255,0.05)', color: tier ? '#A7F432' : 'var(--text-dim)', borderRadius:20, padding:'2px 10px', fontSize:11 }}>{tier||'—'}</span>
                      </td>
                      <td>
                        <span style={{ background: pl.isActive ? 'rgba(167,244,50,0.12)' : 'rgba(255,80,80,0.12)', color: pl.isActive ? '#A7F432' : '#ff6b6b', borderRadius:20, padding:'2px 10px', fontSize:11 }}>{pl.isActive ? 'Active' : 'Suspended'}</span>
                      </td>
                      <td style={{ color:'var(--text-dim)', fontSize:12 }}>{fmtDate(pl.createdAt)}</td>
                      <td>
                        <button className="btn btn-ghost" style={{ fontSize:11, padding:'5px 12px' }} onClick={()=>openPlayer(pl)}>View →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.totalPages>1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={()=>{const np=page-1;setPage(np);load(np,query);}}>← Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=pagination.totalPages} onClick={()=>{const np=page+1;setPage(np);load(np,query);}}>Next →</button>
        </div>
      )}

      {selected && <PlayerModal player={selected} loading={detailLoading} onSuspend={suspendPlayer} onDelete={deletePlayer} onClose={()=>setSelected(null)} />}
    </div>
  );
}
